import { formatDate } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, computed, inject, input, output, signal, viewChild } from '@angular/core';
import {
  CardBodyComponent,
  CardComponent,
  CardHeaderComponent,
  StateMessageComponent,
  TermValueComponent,
  type TermValueItem,
} from '@groundworks/ngx-groundworks-ui';
import { ConfigService } from '../../config.service';
import { ProjectTypeConstants } from '../../constants/project-type.constants';
import { getUserRoles } from '../../models/roles.model';
import { PendingCreditRequestInfo, JobInfo, PaymentLineItem, PaymentSummary } from '../../sdk/sdk';
import { formatUsd } from '../../utils/money';

// The type/method detail label must never be fed into groundworks-term-value as a term —
// the shared component's styleguide CSS unconditionally appends a trailing colon after
// every term, and there's no variant/layout opt-out. dateAmountItems carries just the
// date/amount pair for that component; detailLabel renders as plain markup alongside it.
interface PaymentRowView {
  dateAmountItems: TermValueItem[];
  detailLabel?: string;
  editContext?: PaymentEditContext;
  isEditDisabled?: boolean;
}

export interface PaymentEditContext {
  paymentId: string;
  paymentMethodId?: string;
  /** Display name of the stored type — see the modal's PaymentEditTarget for why it travels. */
  paymentMethodName?: string;
  amount: number;
  paymentDate?: Date;
  checkNumber?: string;
}

interface MergedPaymentRow extends PaymentRowView {
  isPending: boolean;
  sortKey: number;
}

interface MergedPaymentGroup {
  isPending: boolean;
  rows: PaymentRowView[];
}

@Component({
  selector: 'app-payments',
  imports: [
    CardComponent,
    CardBodyComponent,
    CardHeaderComponent,
    StateMessageComponent,
    TermValueComponent,
  ],
  templateUrl: './payments.component.html',
  styleUrl: './payments.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(document:click)': 'onDocumentClick($event)',
    '(document:keydown.escape)': 'closeMenu()',
  },
})
export class PaymentsComponent {
  readonly configService = inject(ConfigService);
  private readonly dropdownRef = viewChild<ElementRef<HTMLElement>>('dropdownRef');

  readonly job = input<JobInfo | undefined>(undefined);
  readonly opportunityId = input<string | undefined>(undefined);
  readonly isLoadingInput = input<boolean>(false);
  readonly hasErrorInput = input<boolean>(false);
  readonly isSendingStatement = input<boolean>(false);
  readonly paymentSummary = input<PaymentSummary | undefined>(undefined);
  readonly pendingCreditRequests = input<PendingCreditRequestInfo[]>([]);

  readonly postPayment = output<void>();
  readonly editPayment = output<PaymentEditContext>();
  readonly accountCreditRequest = output<void>();
  readonly reviewAcr = output<void>();
  readonly sendCustomerStatement = output<void>();
  readonly viewCustomerStatement = output<void>();
  readonly viewPaymentHistory = output<void>();

  readonly isMenuOpen = signal(false);

  readonly isJobCancelled = computed(() => this.job()?.cancelledAt != null);

  readonly state = computed(() => {
    if (this.isLoadingInput()) return 'loading';
    if (this.hasErrorInput()) return 'error';
    if (!this.job()) return 'no-matches';
    return 'matches';
  });

  // View and Send share one gate: a loaded, non-cancelled job. Sending deliberately does not require
  // amountDueNow > 0 — a fully-paid job, or one whose installments aren't due yet, sends a $0
  // statement. The server mirrors this in JobBillingContextResolver.
  readonly showStatementActions = computed(() => !this.isJobCancelled() && this.job() != null);

  get canPostPayment(): boolean {
    const roles = getUserRoles();
    // Accounting can post too — the accounting-only payment types would be unreachable otherwise.
    // Mirrors the RequireGroup any-of on POST /payments/manual; the server is what enforces it.
    return roles.CentralizedSupport || roles.AccountingPostPayment;
  }

  get canEditPayment(): boolean {
    return getUserRoles().EditPayments;
  }

  toggleMenu(): void {
    this.isMenuOpen.update((open) => !open);
  }

  closeMenu(): void {
    this.isMenuOpen.set(false);
  }

  onDocumentClick(event: MouseEvent): void {
    if (!this.isMenuOpen()) return;
    const target = event.target as Node | null;
    const dropdownEl = this.dropdownRef()?.nativeElement;
    if (target && dropdownEl?.contains(target)) return;
    this.closeMenu();
  }

  onPostPayment(): void {
    this.postPayment.emit();
    this.closeMenu();
  }

  onEditPayment(context: PaymentEditContext): void {
    this.editPayment.emit(context);
  }

  onAccountCreditRequest(): void {
    this.accountCreditRequest.emit();
    this.closeMenu();
  }

  onReviewAcr(): void {
    this.reviewAcr.emit();
    this.closeMenu();
  }

  onSendCustomerStatement(): void {
    this.sendCustomerStatement.emit();
    this.closeMenu();
  }

  onViewCustomerStatement(): void {
    this.viewCustomerStatement.emit();
    this.closeMenu();
  }

  onViewPaymentHistory(): void {
    this.viewPaymentHistory.emit();
    this.closeMenu();
  }

  readonly paymentItems = computed<TermValueItem[]>(() => {
    const j = this.job();
    if (!j) return [];

    return [
      ...this.productRows(j),
      ...this.discountRow(j),
      this.promoRow(j),
      ...this.taxRows(j),
      this.contractTotalRow(j),
    ];
  });

  private productRows(j: JobInfo): TermValueItem[] {
    const rows: TermValueItem[] = [];

    // NonInstallableServices projects are excluded here: their money is already represented
    // by the aggregate 'Non-Installable' row below (j.nonInstallableProductGrossTotal), so a
    // per-project row would double-count the same dollars in this line-item list.
    const installableProjects = (j.projects ?? []).filter(
      (project) => project.projectTypeId !== ProjectTypeConstants.NonInstallableServices
    );
    for (const project of installableProjects) {
      rows.push({
        term: project.projectTypeName ? `${project.projectTypeName}` : 'Project',
        value: this.formatCurrency(project.projectGrossAmount),
      });
    }

    const nonInstallableGross = j.nonInstallableProductGrossTotal ?? 0;
    if (nonInstallableGross !== 0) {
      rows.push({ term: 'Non-Installable', value: this.formatCurrency(nonInstallableGross) });
    }

    const recurringGross = j.recurringProductGrossTotal ?? 0;
    if (recurringGross !== 0) {
      rows.push({ term: 'Recurring', value: this.formatCurrency(recurringGross) });
    }

    const grossAmount =
      (j.installableProductGrossTotal ?? 0) +
      (j.nonInstallableProductGrossTotal ?? 0) +
      (j.recurringProductGrossTotal ?? 0);
    rows.push({
      term: 'Products Total (Gross)',
      value: this.formatCurrency(grossAmount),
      variant: 'subtotal',
    });

    return rows;
  }

  // Display the backend-computed contract discount verbatim (Σ active gross − Σ active net).
  // This is the statement-matching figure, distinct from the j.discount promo metadata.
  private discountRow(j: JobInfo): TermValueItem[] {
    const contractDiscount = j.contractDiscount ?? 0;
    if (contractDiscount === 0) return [];
    return [{ term: 'Discount', value: this.formatCurrency(-contractDiscount) }];
  }

  private promoRow(j: JobInfo): TermValueItem {
    const promoAmount = j.promo?.amount ?? 0;
    if (promoAmount === 0) return { term: 'Promo Applied', value: '—' };
    return { term: 'Promo Applied', value: this.formatCurrency(-promoAmount) };
  }

  private taxRows(j: JobInfo): TermValueItem[] {
    return (j.taxes ?? [])
      .filter((tax) => (tax.amount ?? 0) !== 0)
      .map((tax) => ({
        term: tax.taxTypeName ?? 'Taxes',
        value: this.formatCurrency(tax.amount),
      }));
  }

  // Display the backend-computed contract total verbatim (Σ active net + tax).
  private contractTotalRow(j: JobInfo): TermValueItem {
    return {
      term: 'Contract Total (Net)',
      value: this.formatCurrency(j.contractTotal ?? 0),
      variant: 'subtotal',
    };
  }

  readonly mergedPaymentGroups = computed<MergedPaymentGroup[]>(() =>
    this.groupConsecutiveByPending(this.toSortedPaymentRows(this.paymentSummary()))
  );

  private toSortedPaymentRows(summary: PaymentSummary | undefined): MergedPaymentRow[] {
    const posted = (summary?.payments ?? []).map((item) => this.toMergedRow(item, false));
    const pending = (summary?.pendingCredits ?? []).map((item) => this.toMergedRow(item, true));
    return [...posted, ...pending].sort((a, b) => a.sortKey - b.sortKey);
  }

  private toMergedRow(lineItem: PaymentLineItem, isPending: boolean): MergedPaymentRow {
    return {
      isPending,
      sortKey: lineItem.date?.getTime() ?? 0,
      dateAmountItems: [this.toDateAmountItem(lineItem)],
      detailLabel: lineItem.label || undefined,
      editContext: this.toEditContext(lineItem),
      isEditDisabled: !lineItem.isEditWindowOpen,
    };
  }

  private toEditContext(lineItem: PaymentLineItem): PaymentEditContext | undefined {
    if (!lineItem.isEditable || !lineItem.paymentId) return undefined;
    return {
      paymentId: lineItem.paymentId,
      paymentMethodId: lineItem.paymentMethodId,
      // For an editable row the label IS the manual method's name, and the modal needs it: a type
      // gated behind a permission group is filtered out of the caller's methods list, so the name
      // is the only thing the modal can show the stored type under.
      paymentMethodName: lineItem.label,
      amount: lineItem.amount,
      paymentDate: lineItem.date,
      checkNumber: lineItem.checkNumber,
    };
  }

  // Each payment renders as two stacked rows: date/amount (via groundworks-term-value,
  // colon included), then its type/method label directly below with no amount of its
  // own, rendered as plain markup so it does NOT pick up that component's trailing
  // colon — see MOD-4224 follow-up.
  private toDateAmountItem(lineItem: PaymentLineItem): TermValueItem {
    return {
      term: lineItem.date ? formatDate(lineItem.date, 'MM/dd/yyyy', 'en-US', 'UTC') : '',
      value: this.formatCurrency(lineItem.amount),
    };
  }

  // Groups by *consecutive* runs of the same source, not globally — because rows are
  // already date-sorted, an interleaved sequence yields e.g. [posted, pending, posted].
  private groupConsecutiveByPending(rows: MergedPaymentRow[]): MergedPaymentGroup[] {
    const groups: MergedPaymentGroup[] = [];
    for (const row of rows) {
      const last = groups[groups.length - 1];
      const rowView: PaymentRowView = {
        dateAmountItems: row.dateAmountItems,
        detailLabel: row.detailLabel,
        editContext: row.editContext,
        isEditDisabled: row.isEditDisabled,
      };
      if (last && last.isPending === row.isPending) {
        last.rows.push(rowView);
      } else {
        groups.push({ isPending: row.isPending, rows: [rowView] });
      }
    }
    return groups;
  }

  readonly paymentsReceivedItems = computed<TermValueItem[]>(() => [
    {
      term: 'Payments Received',
      value: this.formatCurrency(this.paymentSummary()?.paymentsReceived ?? 0),
      variant: 'subtotal',
    },
  ]);

  readonly totalBalanceDueItems = computed<TermValueItem[]>(() => [
    {
      term: 'Total Balance Due',
      value: this.formatCurrency(this.paymentSummary()?.totalBalanceDue ?? 0),
      variant: 'total',
    },
  ]);

  formatCurrency(amount: number | undefined): string {
    return formatUsd(amount);
  }
}
